import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, ListGroup, Row } from "react-bootstrap";
import { UserRoles } from "../../../constants/UserRoles";
import { useResetPassword, useSendEmailConfirmation } from "../../../hooks/userHooks";
import { useToast } from '../../ui/Toast/ToastProvider.js';

const normalizeInitial = (initialValues) => {
    const roles = initialValues?.roles || initialValues?.roleArray || [];

    return {
        id: initialValues?.id || initialValues?.Id || "",
        email: initialValues?.email || "",
        emailConfirmed: !!initialValues?.emailConfirmed,
        lockedOut: !!initialValues?.lockedOut || !!initialValues?.lockoutEnd,
        roles: roles || [],
    };
};

const listBoxStyle = {
    height: "220px",
    overflowY: "auto",
    border: "1px solid #dee2e6",
    borderRadius: "0.375rem"
};

const UserForm = ({
    mode = "create",
    initialValues = null,
    saving = false,
    onSubmit,
    onCancel
}) => {
    const { sendEmailConfirmation: sendConfirm, loading: sendingConfirm } = useSendEmailConfirmation();
    const { resetPassword, loading: resettingPassword } = useResetPassword();
    const normalizedInitial = useMemo(() => normalizeInitial(initialValues), [initialValues]);
    const { showToast } = useToast();

    const [email, setEmail] = useState("");
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [lockedOut, setLockedOut] = useState(false);
    const [roles, setRoles] = useState([]);
    const [selectedAvailableRoles, setSelectedAvailableRoles] = useState([]);
    const [selectedAssignedRoles, setSelectedAssignedRoles] = useState([]);

    const canSendEmailConfirmation =
        mode !== "create" &&
        !!normalizedInitial?.id &&
        email &&
        emailConfirmed !== true;

    const canResetPassword =
        mode !== "create" &&
        !!normalizedInitial?.id &&
        email;

    const availableRoles = [
        { value: UserRoles.ADMIN, label: "Admin" }
    ];

    const assignedRoleOptions = useMemo(() => {
        const set = new Set((roles || []).map(r => String(r)));
        return availableRoles.filter(r => set.has(r.value));
    }, [roles, availableRoles]);

    const unassignedRoleOptions = useMemo(() => {
        const set = new Set((roles || []).map(r => String(r)));
        return availableRoles.filter(r => !set.has(r.value));
    }, [roles, availableRoles]);

    useEffect(() => {
        if (mode === "create") {
            setEmail("");
            setEmailConfirmed(false);
            setLockedOut(false);
            setRoles([]);
            setSelectedAvailableRoles([]);
            setSelectedAssignedRoles([]);
            return;
        }

        setEmail(normalizedInitial.email);
        setEmailConfirmed(normalizedInitial.emailConfirmed);
        setLockedOut(normalizedInitial.lockedOut);
        setRoles(normalizedInitial.roles || []);
        setSelectedAvailableRoles([]);
        setSelectedAssignedRoles([]);
    }, [mode, normalizedInitial]);

    const addSelectedRole = () => {
        if (selectedAvailableRoles.length === 0) return;

        setRoles(prev => {
            const next = new Set(prev);
            selectedAvailableRoles.forEach(r => next.add(r));
            return Array.from(next);
        });
        setSelectedAvailableRoles([]);
    };

    const removeSelectedRole = () => {
        if (selectedAssignedRoles.length === 0) return;

        setRoles(prev => prev.filter(r => !selectedAssignedRoles.includes(r)));
        setSelectedAssignedRoles([]);
    };

    useEffect(() => {
        if (selectedAvailableRoles.length === 0) return;
        const allowed = new Set(unassignedRoleOptions.map(r => r.value));
        setSelectedAvailableRoles(prev => prev.filter(r => allowed.has(r)));
    }, [unassignedRoleOptions, selectedAvailableRoles.length]);

    useEffect(() => {
        if (selectedAssignedRoles.length === 0) return;
        const allowed = new Set(assignedRoleOptions.map(r => r.value));
        setSelectedAssignedRoles(prev => prev.filter(r => allowed.has(r)));
    }, [assignedRoleOptions, selectedAssignedRoles.length]);

    const toggleAvailableRole = (value) => {
        setSelectedAvailableRoles(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
    };

    const toggleAssignedRole = (value) => {
        setSelectedAssignedRoles(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
    };

    const submit = (e) => {
        e.preventDefault();

        const payload = {
            email,
            emailConfirmed,
            lockedOut,
            roles
        };

        onSubmit(payload);
    };

    const disableAll = saving;

    const handleSendEmailConfirmation = async () => {
        if (!canSendEmailConfirmation) return;
        try { 
            await sendConfirm(normalizedInitial.id);
            showToast("Invitation email sent successfully.", "success");
        } catch (e) {
            showToast(`Failed to send invitation email: ${e?.message || e}`, "error");
        }
    }

    const handleResetPassword = async () => {
        if (!canResetPassword) return;
        try {
            await resetPassword(normalizedInitial.id);
            showToast("Password reset link sent successfully.", "success");
        } catch (e) {
            showToast(`Failed to reset password: ${e?.message || e}`, "error");
        }
    };

    return (
        <Form onSubmit={submit}>
            <Row className="mb-3">
                <Col md={8}>
                    <Row className="mb-3 align-items-end">
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>Email address</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={disableAll}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4} className="d-flex justify-content-end">
                            {canSendEmailConfirmation && (
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={disableAll || sendingConfirm}
                                    onClick={handleSendEmailConfirmation}
                                >
                                    Send invitation email
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {mode !== "create" && (
                        <Row className="mb-3">
                            <Col md={8}>
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    disabled={disableAll || resettingPassword || !normalizedInitial?.id}
                                    onClick={handleResetPassword}
                                >
                                    Send Reset password link to user
                                </Button>
                            </Col>
                        </Row>
                    )}

                    {mode !== "create" && (
                        <Row className="mb-3">
                            <Col md={8}>
                                <Form.Check
                                    type="switch"
                                    id="email-confirmed-switch"
                                    label="Email confirmed"
                                    checked={emailConfirmed}
                                    onChange={(e) => setEmailConfirmed(e.target.checked)}
                                    disabled={disableAll}
                                />
                            </Col>
                        </Row>
                    )}

                    {mode !== "create" && (
                        <Row className="mb-3">
                            <Col md={8}>
                                <Form.Check
                                    type="switch"
                                    id="locked-out-switch"
                                    label="Locked out"
                                    checked={lockedOut}
                                    onChange={(e) => setLockedOut(e.target.checked)}
                                    disabled={disableAll}
                                />
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>

            <Card className="mb-3">
                <Card.Header>Roles</Card.Header>
                <Card.Body>
                    <Row className="align-items-start">
                        <Col md={5}>
                            <div className="mb-2 fw-semibold">Available roles:</div>
                            <div style={listBoxStyle}>
                                <ListGroup variant="flush">
                                    {unassignedRoleOptions.map(r => (
                                        <ListGroup.Item
                                            key={r.value}
                                            active={selectedAvailableRoles.includes(r.value)}
                                            style={{ cursor: "pointer" }}
                                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleAvailableRole(r.value);
                                            }}
                                        >
                                            {r.label}
                                        </ListGroup.Item>
                                    ))}
                                    {unassignedRoleOptions.length === 0 && (
                                        <ListGroup.Item className="text-muted">No available roles</ListGroup.Item>
                                    )}
                                </ListGroup>
                            </div>
                        </Col>

                        <Col md={2} className="d-flex flex-column align-items-center justify-content-center mt-4 mt-md-0">
                            <Button
                                variant="outline-primary"
                                className="mb-2 w-100"
                                type="button"
                                onClick={addSelectedRole}
                                disabled={disableAll || selectedAvailableRoles.length === 0}
                            >
                                Add Role
                            </Button>
                            <Button
                                variant="outline-secondary"
                                className="w-100"
                                type="button"
                                onClick={removeSelectedRole}
                                disabled={disableAll || selectedAssignedRoles.length === 0}
                            >
                                Remove Role
                            </Button>
                        </Col>

                        <Col md={5}>
                            <div className="mb-2 fw-semibold">Assigned roles</div>
                            <div style={listBoxStyle}>
                                <ListGroup variant="flush">
                                    {assignedRoleOptions.map(r => (
                                        <ListGroup.Item
                                            key={r.value}
                                            active={selectedAssignedRoles.includes(r.value)}
                                            style={{ cursor: "pointer" }}
                                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleAssignedRole(r.value);
                                            }}
                                        >
                                            {r.label}
                                        </ListGroup.Item>
                                    ))}
                                    {assignedRoleOptions.length === 0 && (
                                        <ListGroup.Item className="text-muted">No assigned roles</ListGroup.Item>
                                    )}
                                </ListGroup>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="mt-4 d-flex justify-content-start">
                <Button
                    variant="primary"
                    type="submit"
                    disabled={disableAll || !email}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </Button>

                <Button
                    variant="link"
                    type="button"
                    className="ms-2"
                    onClick={onCancel}
                    disabled={disableAll}
                >
                    Cancel
                </Button>
            </div>
        </Form>
    );
};

export default UserForm;
